import { useMemo } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Anchor,
  Badge,
  Divider,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconCalendar,
  IconClock,
  IconMapPin,
  IconPencil,
  IconUsers,
} from "@tabler/icons-react";
import classes from "./SingleIssuanceWizard.module.css";

const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : null);

const Row = ({ icon: Icon, label, value }) => (
  <Group justify="space-between" gap="xs" wrap="nowrap">
    <Group gap={6} wrap="nowrap">
      {Icon ? <Icon size={14} color="#94a3b8" /> : null}
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    </Group>
    <Text size="sm" fw={500} ta="right">
      {value || "—"}
    </Text>
  </Group>
);

const SummaryRail = ({
  quoteData,
  tripPreview,
  coverOption,
  filteredCovers,
  finalAmount,
  discountedAmount,
  isCouponApplied,
  onEditTrip,
  activeStep,
}) => {
  const trip = quoteData?.quote_data;
  const selectedCover = filteredCovers.find((cover) => cover.type === coverOption);
  const sym = quoteData?.currency_symbol || selectedCover?.currency || "₹";
  const baseAmount = quoteData?.total_price?.[coverOption] ?? selectedCover?.price;

  const durationLabel = useMemo(() => {
    if (tripPreview?.duration) return tripPreview.duration;
    if (quoteData?.plan?.duration_days != null) {
      const d = quoteData.plan.duration_days;
      return `${d} day${d === 1 ? "" : "s"}`;
    }
    const start = trip?.start_date ?? tripPreview?.start_date;
    const end = trip?.end_date ?? tripPreview?.end_date;
    if (start && end) {
      const days = dayjs(end).diff(dayjs(start), "day") + 1;
      return days > 0 ? `${days} day${days === 1 ? "" : "s"}` : null;
    }
    return null;
  }, [trip, tripPreview, quoteData?.plan?.duration_days]);

  const totalPayable = finalAmount ?? baseAmount;

  return (
    <div className={classes.summaryCard}>
      <Group justify="space-between" mb="sm">
        <Text fw={700} c="#003594">
          Summary
        </Text>
        {onEditTrip && quoteData ? (
          <Anchor size="sm" c="#003594" onClick={onEditTrip}>
            <Group gap={4}>
              <IconPencil size={12} />
              Edit
            </Group>
          </Anchor>
        ) : null}
      </Group>

      <Stack gap={6}>
        <Row
          icon={IconMapPin}
          label="From"
          value={trip?.travelling_from ?? tripPreview?.travelling_from}
        />
        <Row
          icon={IconMapPin}
          label="To"
          value={trip?.travelling_to ?? tripPreview?.travelling_to}
        />
        <Row
          icon={IconCalendar}
          label="Start"
          value={fmt(trip?.start_date ?? tripPreview?.start_date)}
        />
        <Row
          icon={IconCalendar}
          label="End"
          value={fmt(trip?.end_date ?? tripPreview?.end_date)}
        />
        <Row icon={IconClock} label="Duration" value={durationLabel} />
        <Row
          icon={IconUsers}
          label="Travelers"
          value={trip?.age_fields?.length ?? tripPreview?.travelers}
        />
      </Stack>

      {coverOption && (
        <>
          <Divider my="sm" />
          <Text size="xs" c="dimmed" tt="uppercase" mb={6}>
            Selected Plan
          </Text>
          <Badge color="brand" variant="light" tt="uppercase">
            {coverOption}
          </Badge>
          {baseAmount != null && (
            <Stack gap={4} mt="sm">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Base Amount
                </Text>
                <Text size="sm" fw={500}>
                  {sym} {Number(baseAmount).toLocaleString("en-IN")}
                </Text>
              </Group>
              {isCouponApplied && discountedAmount != null ? (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Discount
                  </Text>
                  <Text size="sm" fw={500} c="green">
                    - {sym} {Number(discountedAmount).toLocaleString("en-IN")}
                  </Text>
                </Group>
              ) : null}
              <div className={classes.summaryTotal}>
                <Group justify="space-between">
                  <Text size="sm" fw={600}>
                    Total Payable
                  </Text>
                  <Text size="md" fw={700} c="#003594">
                    {sym} {Number(totalPayable).toLocaleString("en-IN")}
                  </Text>
                </Group>
              </div>
            </Stack>
          )}
        </>
      )}

      {activeStep === 1 && coverOption ? (
        <Alert color="blue" variant="light" mt="md" py="xs" fz="xs">
          Please review your plan and continue to add traveler details.
        </Alert>
      ) : null}
    </div>
  );
};

export default SummaryRail;
